import { z } from "@/zod";
import {
  apartmentNumber,
  city,
  dateTime,
  nonNegativeDecimalStringSchema,
  postalCode,
  projectDependsOn,
  streetName,
  streetNumber,
  ulid,
} from "@/schemas/commonFields";
import { createNonNegativeDecimalStringSchema } from "@/schemas/commonFields/numberSchema";

const limitFieldsNumberSchema = createNonNegativeDecimalStringSchema(3);

const laborCostsInStoresBase = z.object({
  id: z.string().min(1),
  ref_number: z.coerce.number().nonnegative(),
  store_id: ulid,
  name: z.string().min(1).max(100),
  hours: limitFieldsNumberSchema,
  rate: limitFieldsNumberSchema,
  addition: limitFieldsNumberSchema,
  invoiced_amount: nonNegativeDecimalStringSchema,
  network_id: ulid,
  network: z.string(),
  period_id: ulid,
  period_number: z.coerce.number().nonnegative(),
  period_start: z.string().date(),
  period_end: z.string().date(),
  client_store_number: z.string().min(1),
  postal_code: postalCode,
  street_name: streetName,
  street_number: streetNumber,
  apartment_number: apartmentNumber,
  city: city,
  project_depends_on: projectDependsOn,
});

const laborCostsInStoresExtension = z.object({
  invoice_specification_id: ulid,
  planned_hours: nonNegativeDecimalStringSchema,
  deduction: nonNegativeDecimalStringSchema,
  comment: z.string().optional(),
  include_on_invoice: z.boolean(),
  deleted_at: dateTime.nullable(),
});

export const laborCostsInStoresInitialDepartmentBased =
  laborCostsInStoresBase.extend({
    department_ids: z
      .string()
      .transform((str) => str.split(",").map((id) => id.trim())),
    departments: z
      .string()
      .transform((str) => str.split(",").map((dept) => dept.trim())),
  });

export const laborCostsInStoresInitialRoleBased = laborCostsInStoresBase.extend(
  {
    role_ids: z
      .string()
      .transform((str) => str.split(",").map((id) => id.trim())),
    roles: z
      .string()
      .transform((str) => str.split(",").map((role) => role.trim())),
  },
);

export const laborCostsInStoresDepartmentBasedRead = z
  .object({
    ...laborCostsInStoresInitialDepartmentBased.shape,
    ...laborCostsInStoresExtension.shape,
  })
  .omit({
    period_id: true,
    period_start: true,
    period_end: true,
    period_number: true,
    project_depends_on: true,
    department_ids: true,
  })
  .readonly();

export const laborCostsInStoresRoleBasedRead = z
  .object({
    ...laborCostsInStoresInitialRoleBased.shape,
    ...laborCostsInStoresExtension.shape,
  })
  .omit({
    period_id: true,
    period_start: true,
    period_end: true,
    period_number: true,
    project_depends_on: true,
  })
  .readonly();

export type ReadLaborCostsInStoresDepartmentBased = z.infer<
  typeof laborCostsInStoresDepartmentBasedRead
>;

export type ReadLaborCostsInStoresRoleBased = z.infer<
  typeof laborCostsInStoresRoleBasedRead
>;

export type ReadLaborCostsInStores =
  | ReadLaborCostsInStoresDepartmentBased
  | ReadLaborCostsInStoresRoleBased;
